import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';
import {GateweeSelectV3Component} from '../../gatewee-select-v3/gatewee-select-v3.component';
import {Integration} from '@gatewee/types/integration';
import {
  CreateIntegrationRoutingRulePayload,
  IntegrationRoutingRule,
  UpdateIntegrationRoutingRulePayload,
} from '@gatewee/types/integration-routing-rule';
import {PaymentMethod} from '@gatewee/types/payment';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {TuiCheckbox} from '@taiga-ui/kit';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';

export interface IntegrationRoutingRuleFormDialogData {
  walletId: number;
  routableIntegrations: Integration[];
  editRule: IntegrationRoutingRule | null;
}

export type IntegrationRoutingRuleFormResult =
  | {kind: 'create'; payload: CreateIntegrationRoutingRulePayload}
  | {
      kind: 'edit';
      id: number;
      payload: UpdateIntegrationRoutingRulePayload;
    };

@Component({
  selector: 'app-integration-routing-rule-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
    GateweeSelectV3Component,
    TuiCheckbox,
  ],
  templateUrl: './integration-routing-rule-form.component.html',
})
export class IntegrationRoutingRuleFormComponent implements OnInit {
  readonly paymentMethods: PaymentMethod[] = Object.values(PaymentMethod);

  targetIntegrationItems: {value: number; label: string}[] = [];
  paymentMethodItems: {value: string; label: string}[] = [];
  targetIntegrationSelectErrorMessages: Record<string, string> = {};

  form!: FormGroup;

  constructor(
    private readonly fb: FormBuilder,
    private readonly translate: TranslateService,
    private readonly alerts: TuiAlertService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<
      IntegrationRoutingRuleFormResult | void,
      IntegrationRoutingRuleFormDialogData
    >,
  ) {}

  get data(): IntegrationRoutingRuleFormDialogData {
    return this.context.data;
  }

  get isEdit(): boolean {
    return !!this.data.editRule;
  }

  get noRoutingTargets(): boolean {
    return !this.data.routableIntegrations?.length;
  }

  ngOnInit(): void {
    const rule = this.data.editRule;
    this.form = this.fb.group(
      {
        targetIntegrationId: [
          rule?.targetIntegration?.id ?? null,
          [Validators.required],
        ],
        paymentMethod: [rule?.paymentMethod ?? ''],
        minAmount: [this.toInputNumber(rule?.minAmount ?? null)],
        maxAmount: [this.toInputNumber(rule?.maxAmount ?? null)],
        priority: [rule?.priority ?? 0, [Validators.required]],
        description: [rule?.description ?? ''],
        isActive: [rule?.isActive ?? true],
      },
      {validators: [this.amountRangeValidator.bind(this)]},
    );

    this.targetIntegrationItems = this.data.routableIntegrations.map(
      integration => ({
        value: integration.id,
        label: this.integrationLabel(integration),
      }),
    );

    this.paymentMethodItems = [
      {
        value: '',
        label: this.translate.instant(
          'features.wallets.integrationRouting.form.anyMethod',
        ),
      },
      ...this.paymentMethods.map(m => ({
        value: m,
        label: this.translate.instant(`enums.paymentMethod.${m}`),
      })),
    ];

    if (this.noRoutingTargets) {
      this.form.disable({emitEvent: false});
      this.alerts
        .open(
          this.translate.instant(
            'features.wallets.integrationRouting.noRoutableIntegrations',
          ),
          {
            label: this.translate.instant(
              'features.wallets.integrationRouting.noRoutableIntegrationsTitle',
            ),
            appearance: 'warning',
          },
        )
        .subscribe();
    }

    this.targetIntegrationSelectErrorMessages = {
      required: this.translate.instant(
        'features.wallets.integrationRouting.errors.targetRequired',
      ),
    };
  }

  private integrationLabel(integration: Integration): string {
    const providerLabel = this.translate.instant(
      `enums.paymentProvider.${integration.provider}`,
    );
    const suffix = integration.description?.trim()
      ? ` — ${integration.description.trim()}`
      : '';
    return `#${integration.id} · ${providerLabel}${suffix}`;
  }

  private amountRangeValidator(
    group: AbstractControl,
  ): ValidationErrors | null {
    const minN = this.amountBoundForRange(group.get('minAmount')?.value);
    const maxN = this.amountBoundForRange(group.get('maxAmount')?.value);
    if (minN !== null && maxN !== null && minN >= maxN) {
      return {amountRange: true};
    }
    return null;
  }

  private amountBoundForRange(v: unknown): number | null {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return null;
    return n;
  }

  private toInputNumber(v: unknown): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(String(v));
    if (!Number.isFinite(n)) return null;
    if (n === 0) return null;
    return n;
  }

  submit(): void {
    if (this.noRoutingTargets) return;
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    const paymentMethodRaw = raw.paymentMethod as string;
    const paymentMethod: PaymentMethod | null | undefined =
      paymentMethodRaw === '' ? undefined : (paymentMethodRaw as PaymentMethod);

    const minAmount = this.normalizeAmount(raw.minAmount);
    const maxAmount = this.normalizeAmount(raw.maxAmount);

    if (this.isEdit && this.data.editRule) {
      const payload: UpdateIntegrationRoutingRulePayload = {
        targetIntegrationId: Number(raw.targetIntegrationId),
        paymentMethod: paymentMethod ?? null,
        minAmount,
        maxAmount,
        priority: Number(raw.priority),
        description: raw.description ? String(raw.description) : null,
        isActive: !!raw.isActive,
      };
      this.context.completeWith({
        kind: 'edit',
        id: this.data.editRule.id,
        payload,
      });
      return;
    }

    const payload: CreateIntegrationRoutingRulePayload = {
      walletId: +this.data.walletId,
      targetIntegrationId: Number(raw.targetIntegrationId),
      priority: Number(raw.priority),
      description: raw.description ? String(raw.description) : null,
    };
    if (paymentMethod !== undefined) {
      payload.paymentMethod = paymentMethod ?? null;
    }
    if (minAmount !== null) payload.minAmount = minAmount;
    if (maxAmount !== null) payload.maxAmount = maxAmount;

    this.context.completeWith({kind: 'create', payload});
  }

  private normalizeAmount(v: unknown): number | null {
    if (v === '' || v === null || v === undefined) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return null;
    return n;
  }

  cancel(): void {
    this.context.completeWith();
  }

  amountRangeError(): boolean {
    const g = this.form;
    if (!g) return false;
    return !!g.errors?.['amountRange'] && g.touched;
  }
}
